import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useActorEmails, fmtAuthor } from "@/hooks/use-actor-emails";

export const Route = createFileRoute("/_authenticated/admin/cursos")({
  
  head: () => ({ meta: [{ title: "Admin — Cursos | Mine Class" }, { name: "description", content: "Panel de administración de Cursos — acceso restringido a Mine Class." }, { name: "robots", content: "noindex, nofollow" }] }),
  component: CoursesAdmin,
});

type CourseType = "abierto" | "empresa" | "medida" | "certificacion";

type Course = {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  category: string | null;
  course_type: CourseType;
  price_clp: number | null;
  price_usd: number | null;
  image_url: string | null;
  external_url: string | null;
  is_active: boolean;
  is_featured: boolean;
  created_by: string | null;
  updated_by: string | null;
};

const empty: Partial<Course> = {
  slug: "", title: "", description: "", course_type: "abierto",
  image_url: "", is_active: true,
};


function slugify(s: string) {
  return s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "").slice(0, 80);
}

function CoursesAdmin() {
  const [items, setItems] = useState<Course[]>([]);
  const [editing, setEditing] = useState<Partial<Course> | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const lookup = useActorEmails(items.flatMap((c) => [c.created_by, c.updated_by]));

  async function load() {
    const { data } = await supabase.from("courses").select("*").order("created_at", { ascending: false });
    setItems((data ?? []) as Course[]);
  }
  useEffect(() => { load(); }, []);


  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !editing) return;
    setBusy(true);
    try {
      const userId = (await supabase.auth.getUser()).data.user?.id;
      const path = `${userId}/cursos/${Date.now()}-${file.name.replace(/[^a-zA-Z0-9._-]/g, "_")}`;
      const { error } = await supabase.storage.from("media").upload(path, file, { contentType: file.type });
      if (error) throw error;
      const { data } = await supabase.storage.from("media").createSignedUrl(path, 60 * 60 * 24 * 365 * 5);
      setEditing({ ...editing, image_url: data?.signedUrl ?? "" });
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Error");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function save() {
    if (!editing) return;
    const slug = editing.slug || slugify(editing.title ?? "");
    const payload = {
      slug, title: editing.title ?? "",
      description: editing.description || null,
      course_type: (editing.course_type ?? "abierto") as CourseType,
      image_url: editing.image_url || null,
      is_active: editing.is_active ?? true,
      category: null,
      price_clp: editing.price_clp ?? null,
      price_usd: editing.price_usd ?? null,
      external_url: editing.external_url || null,
      is_featured: editing.is_featured ?? false,
    };
    const q = editing.id ? supabase.from("courses").update(payload).eq("id", editing.id)
                         : supabase.from("courses").insert(payload);
    const { error } = await q;
    if (error) { setErr(error.message); return; }
    setEditing(null);
    load();
  }


  async function toggleActive(c: Course) {
    await supabase.from("courses").update({ is_active: !c.is_active }).eq("id", c.id);
    load();
  }

  async function remove(c: Course) {
    if (!confirm(`Eliminar curso "${c.title}"?`)) return;
    await supabase.from("courses").delete().eq("id", c.id);
    load();
  }

  if (editing) {
    return (
      <div className="bg-white border border-[var(--color-outline-variant)] p-6 space-y-4 max-w-3xl">
        <h2 className="font-bold text-lg">{editing.id ? "Editar" : "Nuevo"} curso</h2>
        <Field label="Título">
          <input value={editing.title ?? ""} onChange={(e) => setEditing({ ...editing, title: e.target.value, slug: editing.id ? editing.slug : slugify(e.target.value) })}
            className="w-full border border-[var(--color-outline-variant)] px-3 py-2" />
        </Field>
        <Field label="Slug"><input value={editing.slug ?? ""} onChange={(e) => setEditing({ ...editing, slug: slugify(e.target.value) })}
          className="w-full border border-[var(--color-outline-variant)] px-3 py-2 font-mono text-sm" /></Field>
        <Field label="Sección donde se muestra">
          <select
            value={editing.course_type ?? "abierto"}
            onChange={(e) => setEditing({ ...editing, course_type: e.target.value as CourseType })}
            className="w-full border border-[var(--color-outline-variant)] px-3 py-2"
          >
            <option value="abierto">Cursos para Personas (/cursos/personas)</option>
            <option value="empresa">Cursos para Empresas (/cursos/empresas)</option>
            <option value="certificacion">Certificación Mine Class (/cursos/personas/certificacion-mine-class)</option>
            <option value="medida">Cursos a Medida (interno)</option>
          </select>
        </Field>
        <Field label="Descripción"><textarea value={editing.description ?? ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })}
          rows={4} className="w-full border border-[var(--color-outline-variant)] px-3 py-2" /></Field>
        <Field label="Imagen (opcional)">
          <div className="space-y-2">
            {editing.image_url && <img src={editing.image_url} alt="" className="w-full max-w-md aspect-video object-cover" />}
            <input ref={fileRef} type="file" accept="image/*" onChange={onFile} disabled={busy} className="text-sm" />
          </div>
        </Field>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Precio CLP (opcional)">
            <input
              type="number"
              value={editing.price_clp ?? ""}
              onChange={(e) => setEditing({ ...editing, price_clp: e.target.value ? Number(e.target.value) : null })}
              className="w-full border border-[var(--color-outline-variant)] px-3 py-2"
              placeholder="Ej: 150000"
            />
          </Field>
          <Field label="Precio USD (opcional)">
            <input
              type="number"
              value={editing.price_usd ?? ""}
              onChange={(e) => setEditing({ ...editing, price_usd: e.target.value ? Number(e.target.value) : null })}
              className="w-full border border-[var(--color-outline-variant)] px-3 py-2"
              placeholder="Ej: 180"
            />
          </Field>
        </div>
        <Field label="Enlace de compra / inscripción (opcional)">
          <input
            value={editing.external_url ?? ""}
            onChange={(e) => setEditing({ ...editing, external_url: e.target.value })}
            className="w-full border border-[var(--color-outline-variant)] px-3 py-2 text-sm"
            placeholder="https://umayor.mine-class.com/programa/..."
          />
        </Field>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={editing.is_featured ?? false}
            onChange={(e) => setEditing({ ...editing, is_featured: e.target.checked })}
          />
          Destacar como Diplomado (Certificación Universidad Mayor)
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={editing.is_active ?? true}
            onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })}
          />
          Publicado (desmarcar para dar de baja del sitio)
        </label>

        {err && <p className="text-sm text-red-600">{err}</p>}
        <div className="flex gap-2">
          <button onClick={save} className="px-4 py-2 bg-[var(--color-brand-coral)] text-white font-bold">Guardar</button>
          <button onClick={() => setEditing(null)} className="px-4 py-2 border border-[var(--color-outline-variant)]">Cancelar</button>
        </div>
      </div>
    );
  }

  return (

    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="font-bold text-lg">Cursos ({items.length})</h2>
        <button onClick={() => setEditing({ ...empty })} className="px-4 py-2 bg-[var(--color-brand-coral)] text-white font-bold">+ Nuevo curso</button>
      </div>
      <div className="bg-white border border-[var(--color-outline-variant)]">
        <table className="w-full text-sm">
          <thead className="bg-[var(--color-surface-container-low)] text-left">
            <tr><th className="px-4 py-3">Título</th><th className="px-4 py-3">Sección</th><th className="px-4 py-3">Categoría</th><th className="px-4 py-3">Precio</th><th className="px-4 py-3">Estado</th><th className="px-4 py-3">Autoría</th><th></th></tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id} className="border-t border-[var(--color-outline-variant)]">
                <td className="px-4 py-3 font-semibold">{c.title}{c.is_featured && <span className="ml-2 text-xs text-[var(--color-brand-coral)]">★</span>}</td>
                <td className="px-4 py-3 text-xs">{c.course_type === "abierto" ? "Abiertos" : c.course_type === "empresa" ? "Empresas" : c.course_type === "certificacion" ? "Certificación Mine Class" : "A Medida"}</td>
                <td className="px-4 py-3 text-xs">{c.category ?? "—"}</td>
                <td className="px-4 py-3 font-mono text-xs">{c.price_clp ? `$${c.price_clp.toLocaleString("es-CL")}` : "—"}</td>
                <td className="px-4 py-3 text-xs">{c.is_active ? <span className="text-green-700 font-bold">Activo</span> : <span className="text-[var(--color-on-surface-variant)]">Dado de baja</span>}</td>
                <td className="px-4 py-3 text-[11px] text-[var(--color-on-surface-variant)]">{fmtAuthor(lookup, c.created_by, c.updated_by)}</td>
                <td className="px-4 py-3 text-right space-x-3">
                  <button onClick={() => setEditing(c)} className="text-xs underline">Editar</button>
                  <button onClick={() => toggleActive(c)} className="text-xs underline">{c.is_active ? "Dar de baja" : "Reactivar"}</button>
                  <button onClick={() => remove(c)} className="text-xs text-red-600">Eliminar</button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-[var(--color-on-surface-variant)]">Sin cursos aún.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="text-xs font-semibold uppercase tracking-widest text-[var(--color-on-surface-variant)] mb-1">{label}</div>
      {children}
    </label>
  );
}
